import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Truck, BookOpen, Crown, Heart, Zap, Share2,
  CreditCard, AlertCircle, TrendingUp, TrendingDown,
  DollarSign, Users, BarChart3, Clock, RotateCcw
} from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return s || '-'; } };
const fmtDateTime = (s) => { try { return new Date(s).toLocaleString('id-ID', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return s || '-'; } };

function StatCard({ icon: Icon, label, value, sub, color = 'text-[#2D6CDF]', bg = 'bg-blue-50' }) {
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="p-5">
        <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mb-3`}>
          <Icon size={18} className={color} />
        </div>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        <p className="text-xs text-slate-500 mt-0.5">{label}</p>
        {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function ProgressBar({ value, max, color = 'bg-[#2D6CDF]' }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-slate-100 rounded-full h-2">
        <div className={`h-2 rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-slate-500 w-8 text-right">{pct.toFixed(0)}%</span>
    </div>
  );
}

function EmptyState({ label }) {
  return <div className="py-12 text-center text-slate-400 text-sm">{label}</div>;
}

function StatusBadge({ status }) {
  const map = {
    success: 'bg-emerald-100 text-emerald-700',
    delivered: 'bg-emerald-100 text-emerald-700',
    paid: 'bg-emerald-100 text-emerald-700',
    approved: 'bg-blue-100 text-blue-700',
    active: 'bg-emerald-100 text-emerald-700',
    pending: 'bg-amber-100 text-amber-700',
    failed: 'bg-red-100 text-red-700',
    expired: 'bg-slate-100 text-slate-600',
    cancelled: 'bg-red-100 text-red-600',
    completed: 'bg-violet-100 text-violet-700',
  };
  return <Badge className={`${map[status] || 'bg-slate-100 text-slate-600'} border-0 text-xs`}>{status}</Badge>;
}

// ───────────────────────── COD ─────────────────────────
function CODReport({ period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/tenant/reports/cod?period=${period}`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [period]);

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;
  if (!data) return <EmptyState label="Gagal memuat data" />;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Truck} label="Total Pesanan COD" value={data.total_orders} color="text-amber-600" bg="bg-amber-50" />
        <StatCard icon={TrendingUp} label="Terkirim" value={data.delivered} color="text-emerald-600" bg="bg-emerald-50" />
        <StatCard icon={Clock} label="Pending" value={data.pending} color="text-amber-500" bg="bg-amber-50" />
        <StatCard icon={DollarSign} label="Revenue COD" value={`Rp ${fmt(data.total_revenue)}`} color="text-[#2D6CDF]" bg="bg-blue-50" />
      </div>
      <Card className="border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-base">Pesanan COD Terakhir</CardTitle></CardHeader>
        <CardContent className="p-0">
          {!data.orders?.length ? <EmptyState label="Belum ada pesanan COD" /> : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Pelanggan</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Total</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Status COD</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Tanggal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {data.orders.map(o => (
                  <tr key={o.id} className="hover:bg-slate-50/50">
                    <td className="py-3 px-4 text-slate-700">{o.customer_name || '—'}</td>
                    <td className="py-3 px-4 font-medium">Rp {fmt(o.total)}</td>
                    <td className="py-3 px-4"><StatusBadge status={o.cod_status || 'pending'} /></td>
                    <td className="py-3 px-4 text-slate-500">{fmtDate(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ───────────────────────── COURSES ─────────────────────────
function CourseReport({ period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/tenant/reports/courses?period=${period}`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [period]);

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;
  if (!data) return <EmptyState label="Gagal memuat data" />;

  const maxRevenue = Math.max(...(data.courses || []).map(c => c.revenue), 1);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard icon={BookOpen} label="Total Kursus" value={data.total_courses} color="text-violet-600" bg="bg-violet-50" />
        <StatCard icon={Users} label="Enrollment Periode" value={data.total_enrollments} color="text-blue-600" bg="bg-blue-50" />
        <StatCard icon={DollarSign} label="Revenue Kursus" value={`Rp ${fmt(data.total_revenue)}`} color="text-emerald-600" bg="bg-emerald-50" />
      </div>
      <Card className="border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-base">Revenue per Kursus</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {!data.courses?.length ? <EmptyState label="Belum ada kursus" /> :
            data.courses.map(c => (
              <div key={c.id}>
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{c.title}</p>
                    <p className="text-xs text-slate-400">{c.period_enrollments} enrollment • {c.completion_rate}% selesai</p>
                  </div>
                  <span className="text-sm font-bold text-slate-900 ml-4">Rp {fmt(c.revenue)}</span>
                </div>
                <ProgressBar value={c.revenue} max={maxRevenue} color="bg-violet-500" />
              </div>
            ))}
        </CardContent>
      </Card>
      <Card className="border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-base">Detail per Kursus</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="py-3 px-4 text-left font-medium text-slate-600">Kursus</th>
                <th className="py-3 px-4 text-left font-medium text-slate-600">Total Enroll</th>
                <th className="py-3 px-4 text-left font-medium text-slate-600">Selesai</th>
                <th className="py-3 px-4 text-left font-medium text-slate-600">Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {data.courses.map(c => (
                <tr key={c.id} className="hover:bg-slate-50/50">
                  <td className="py-3 px-4 font-medium text-slate-800">{c.title}</td>
                  <td className="py-3 px-4 text-slate-600">{c.total_enrollments}</td>
                  <td className="py-3 px-4">{c.completed} <span className="text-slate-400 text-xs">({c.completion_rate}%)</span></td>
                  <td className="py-3 px-4 font-medium">Rp {fmt(c.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ───────────────────────── MEMBERSHIP ─────────────────────────
function MembershipReport({ period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/tenant/reports/memberships?period=${period}`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [period]);

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;
  if (!data) return <EmptyState label="Gagal memuat data" />;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Crown} label="Member Aktif" value={data.active} color="text-amber-600" bg="bg-amber-50" />
        <StatCard icon={TrendingDown} label="Churn Rate" value={`${data.churn_rate}%`} color="text-red-500" bg="bg-red-50" sub={`${data.expired + data.cancelled} expired/batal`} />
        <StatCard icon={Users} label="Baru (Periode)" value={data.new_in_period} color="text-blue-600" bg="bg-blue-50" />
        <StatCard icon={AlertCircle} label="Segera Berakhir" value={data.expiring_soon} color="text-orange-500" bg="bg-orange-50" sub="dalam 30 hari" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Status pie - simple bars */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader><CardTitle className="text-base">Distribusi Status</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {[
              { label: 'Aktif', value: data.active, color: 'bg-emerald-500' },
              { label: 'Expired', value: data.expired, color: 'bg-slate-300' },
              { label: 'Dibatalkan', value: data.cancelled, color: 'bg-red-400' },
            ].map(item => (
              <div key={item.label}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">{item.label}</span>
                  <span className="font-medium">{item.value}</span>
                </div>
                <ProgressBar value={item.value} max={data.total || 1} color={item.color} />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Per plan */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader><CardTitle className="text-base">Per Paket Membership</CardTitle></CardHeader>
          <CardContent className="p-0">
            {!data.plans?.length ? <EmptyState label="Belum ada data paket" /> : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="py-2 px-4 text-left font-medium text-slate-600">Paket</th>
                    <th className="py-2 px-4 text-left font-medium text-slate-600">Aktif</th>
                    <th className="py-2 px-4 text-left font-medium text-slate-600">Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {data.plans.map((p, i) => (
                    <tr key={i} className="hover:bg-slate-50/50">
                      <td className="py-2 px-4 font-medium">{p.plan_name || p._id || '—'}</td>
                      <td className="py-2 px-4">{p.active}</td>
                      <td className="py-2 px-4">Rp {fmt(p.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Member list */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-base">Member Aktif Terbaru</CardTitle></CardHeader>
        <CardContent className="p-0">
          {!data.members?.length ? <EmptyState label="Belum ada member aktif" /> : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Member</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Paket</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Berlaku</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {data.members.map(m => (
                  <tr key={m.id} className="hover:bg-slate-50/50">
                    <td className="py-3 px-4">{m.customer_name || m.customer_email || '—'}</td>
                    <td className="py-3 px-4 text-slate-500">{m.plan_name || '—'}</td>
                    <td className="py-3 px-4 text-slate-500">{fmtDate(m.expires_at)}</td>
                    <td className="py-3 px-4"><StatusBadge status={m.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ───────────────────────── DONATION ─────────────────────────
function DonationReport({ period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/tenant/reports/donations?period=${period}`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [period]);

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;
  if (!data) return <EmptyState label="Gagal memuat data" />;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard icon={Heart} label="Total Donasi Terkumpul" value={`Rp ${fmt(data.total_raised)}`} color="text-rose-500" bg="bg-rose-50" />
        <StatCard icon={Users} label="Donatur (Periode)" value={data.total_donors} color="text-blue-600" bg="bg-blue-50" />
        <StatCard icon={BarChart3} label="Total Campaign" value={data.total_campaigns} color="text-violet-600" bg="bg-violet-50" />
      </div>
      <Card className="border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-base">Per Kampanye Donasi</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          {!data.campaigns?.length ? <EmptyState label="Belum ada kampanye donasi" /> :
            data.campaigns.map(c => (
              <div key={c.id} className="space-y-1">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{c.title}</p>
                    <p className="text-xs text-slate-400">{c.period_donors} donatur • rata-rata Rp {fmt(c.avg_donation)}</p>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-sm font-bold">Rp {fmt(c.raised)}</p>
                    {c.goal_amount > 0 && <p className="text-xs text-slate-400">dari Rp {fmt(c.goal_amount)}</p>}
                  </div>
                </div>
                {c.goal_amount > 0 && <ProgressBar value={c.raised} max={c.goal_amount} color="bg-rose-500" />}
              </div>
            ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ───────────────────────── PPOB ─────────────────────────
function PPOBReport({ period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/tenant/reports/ppob?period=${period}`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [period]);

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;
  if (!data) return <EmptyState label="Gagal memuat data" />;

  const maxCount = Math.max(...(data.by_type || []).map(t => t.count), 1);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Zap} label="Total Transaksi" value={data.total} color="text-amber-500" bg="bg-amber-50" />
        <StatCard icon={TrendingUp} label="Sukses" value={data.success} color="text-emerald-600" bg="bg-emerald-50" sub={`${data.success_rate}% success rate`} />
        <StatCard icon={DollarSign} label="Total Nominal" value={`Rp ${fmt(data.total_amount)}`} color="text-blue-600" bg="bg-blue-50" />
        <StatCard icon={DollarSign} label="Fee/Margin" value={`Rp ${fmt(data.total_fee)}`} color="text-violet-600" bg="bg-violet-50" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-slate-200 shadow-sm">
          <CardHeader><CardTitle className="text-base">Per Kategori Produk</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {!data.by_type?.length ? <EmptyState label="Belum ada data" /> :
              data.by_type.map((t, i) => (
                <div key={i}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-700 capitalize">{t._id || 'lainnya'}</span>
                    <span className="font-medium">{t.count} txn</span>
                  </div>
                  <ProgressBar value={t.count} max={maxCount} color="bg-amber-400" />
                </div>
              ))}
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader><CardTitle className="text-base">Transaksi Terbaru</CardTitle></CardHeader>
          <CardContent className="p-0">
            {!data.recent?.length ? <EmptyState label="Belum ada transaksi" /> : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="py-2 px-4 text-left font-medium text-slate-600">Produk</th>
                    <th className="py-2 px-4 text-left font-medium text-slate-600">Nominal</th>
                    <th className="py-2 px-4 text-left font-medium text-slate-600">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {data.recent.slice(0, 10).map(t => (
                    <tr key={t.id} className="hover:bg-slate-50/50">
                      <td className="py-2 px-4 text-slate-700">{t.product_name || t.product_code || '—'}</td>
                      <td className="py-2 px-4">Rp {fmt(t.amount)}</td>
                      <td className="py-2 px-4"><StatusBadge status={t.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ───────────────────────── AFFILIATE ─────────────────────────
function AffiliateReport({ period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/tenant/reports/affiliate?period=${period}`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [period]);

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;
  if (!data) return <EmptyState label="Gagal memuat data" />;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Share2} label="Total Komisi" value={data.total_commissions} color="text-emerald-600" bg="bg-emerald-50" />
        <StatCard icon={DollarSign} label="Total Amount" value={`Rp ${fmt(data.total_amount)}`} color="text-blue-600" bg="bg-blue-50" />
        <StatCard icon={Clock} label="Pending" value={`Rp ${fmt(data.payable_amount)}`} color="text-amber-500" bg="bg-amber-50" sub="harus dibayar" />
        <StatCard icon={TrendingUp} label="Sudah Dibayar" value={data.paid} color="text-violet-600" bg="bg-violet-50" />
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-base">Top Affiliate</CardTitle></CardHeader>
        <CardContent className="p-0">
          {!data.per_affiliate?.length ? <EmptyState label="Belum ada data affiliate" /> : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Affiliate</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Kode</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Total Komisi</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Pending</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Dibayar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {data.per_affiliate.map((a, i) => (
                  <tr key={i} className="hover:bg-slate-50/50">
                    <td className="py-3 px-4 font-medium">{a.affiliate_name || a._id || '—'}</td>
                    <td className="py-3 px-4 font-mono text-slate-500">{a.affiliate_code || '—'}</td>
                    <td className="py-3 px-4 font-bold">Rp {fmt(a.total_commission)}</td>
                    <td className="py-3 px-4 text-amber-600">Rp {fmt(a.pending)}</td>
                    <td className="py-3 px-4 text-emerald-600">Rp {fmt(a.paid)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-base">Komisi Terbaru</CardTitle></CardHeader>
        <CardContent className="p-0">
          {!data.recent?.length ? <EmptyState label="Belum ada komisi" /> : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Affiliate</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Order</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Komisi</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Status</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Tanggal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {data.recent.slice(0, 15).map(c => (
                  <tr key={c.id} className="hover:bg-slate-50/50">
                    <td className="py-3 px-4">{c.affiliate_name || '—'}</td>
                    <td className="py-3 px-4 font-mono text-xs text-slate-500">{c.order_id?.slice(-8) || '—'}</td>
                    <td className="py-3 px-4 font-medium">Rp {fmt(c.commission_amount)}</td>
                    <td className="py-3 px-4"><StatusBadge status={c.status} /></td>
                    <td className="py-3 px-4 text-slate-500">{fmtDate(c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ───────────────────────── PAYMENT PLANS (DP/TERMIN) ─────────────────────────
function PaymentPlanReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get('/tenant/reports/payment-plans').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;
  if (!data) return <EmptyState label="Gagal memuat data" />;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={CreditCard} label="Total Rencana" value={data.total_plans} color="text-blue-600" bg="bg-blue-50" />
        <StatCard icon={TrendingUp} label="Aktif" value={data.active_plans} color="text-emerald-600" bg="bg-emerald-50" />
        <StatCard icon={DollarSign} label="Outstanding" value={`Rp ${fmt(data.outstanding_amount)}`} color="text-amber-500" bg="bg-amber-50" sub="total belum lunas" />
        <StatCard icon={AlertCircle} label="Overdue" value={`Rp ${fmt(data.overdue_amount)}`} color="text-red-500" bg="bg-red-50" sub={`${data.overdue_schedules_count} termin`} />
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-base">Rencana Pembayaran</CardTitle></CardHeader>
        <CardContent className="p-0">
          {!data.plans?.length ? <EmptyState label="Belum ada rencana pembayaran" /> : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Pelanggan</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Termin</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Outstanding</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Overdue</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {data.plans.map(p => (
                  <tr key={p.id} className="hover:bg-slate-50/50">
                    <td className="py-3 px-4 font-medium text-slate-800">{p.customer_name || '—'}</td>
                    <td className="py-3 px-4 text-slate-500">{p.paid_installments}/{p.total_installments}</td>
                    <td className="py-3 px-4 font-medium text-amber-600">Rp {fmt(p.outstanding_amount)}</td>
                    <td className="py-3 px-4">
                      {p.overdue_amount > 0
                        ? <span className="font-bold text-red-500">Rp {fmt(p.overdue_amount)}</span>
                        : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="py-3 px-4"><StatusBadge status={p.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ───────────────────────── OVERDUE INVOICES ─────────────────────────
function OverdueInvoiceReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get('/tenant/reports/overdue-invoices').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;
  if (!data) return <EmptyState label="Gagal memuat data" />;

  const agingLabels = {
    '0_7': '0-7 hari',
    '8_30': '8-30 hari',
    '31_60': '31-60 hari',
    '60_plus': '> 60 hari',
  };
  const agingColors = {
    '0_7': 'bg-amber-400',
    '8_30': 'bg-orange-500',
    '31_60': 'bg-red-500',
    '60_plus': 'bg-red-800',
  };
  const maxAging = Math.max(...Object.values(data.aging_amounts || {}), 1);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard icon={AlertCircle} label="Invoice Overdue" value={data.overdue_count} color="text-red-500" bg="bg-red-50" sub={`Rp ${fmt(data.total_overdue_amount)}`} />
        <StatCard icon={Clock} label="Jatuh Tempo 7 Hari" value={data.upcoming_count} color="text-amber-500" bg="bg-amber-50" sub={`Rp ${fmt(data.total_upcoming_amount)}`} />
        <StatCard icon={DollarSign} label="Total Outstanding" value={`Rp ${fmt(data.total_outstanding)}`} color="text-slate-600" bg="bg-slate-100" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Aging analysis */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader><CardTitle className="text-base">Analisis Aging</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(agingLabels).map(([key, label]) => (
              <div key={key}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-700">{label}</span>
                  <span className="font-medium">{data.aging_buckets[key] || 0} invoice • Rp {fmt(data.aging_amounts[key] || 0)}</span>
                </div>
                <ProgressBar value={data.aging_amounts[key] || 0} max={maxAging} color={agingColors[key]} />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Upcoming */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader><CardTitle className="text-base">Akan Jatuh Tempo (7 hari)</CardTitle></CardHeader>
          <CardContent className="p-0">
            {!data.upcoming_invoices?.length ? <EmptyState label="Tidak ada invoice yang akan jatuh tempo" /> : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="py-2 px-4 text-left font-medium text-slate-600">No. Invoice</th>
                    <th className="py-2 px-4 text-left font-medium text-slate-600">Total</th>
                    <th className="py-2 px-4 text-left font-medium text-slate-600">Jatuh Tempo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {data.upcoming_invoices.map(inv => (
                    <tr key={inv.id} className="hover:bg-slate-50/50">
                      <td className="py-2 px-4 font-mono text-xs text-slate-700">{inv.invoice_number || inv.id.slice(-8)}</td>
                      <td className="py-2 px-4 font-medium">Rp {fmt(inv.total)}</td>
                      <td className="py-2 px-4 text-amber-600 font-medium">{fmtDate(inv.due_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Overdue list */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <AlertCircle size={16} className="text-red-500" /> Invoice Overdue
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {!data.overdue_invoices?.length ? <EmptyState label="Tidak ada invoice overdue" /> : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">No. Invoice</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Total</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Jatuh Tempo</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {data.overdue_invoices.map(inv => (
                  <tr key={inv.id} className="hover:bg-rose-50/30">
                    <td className="py-3 px-4 font-mono text-xs text-slate-700">{inv.invoice_number || inv.id.slice(-8)}</td>
                    <td className="py-3 px-4 font-bold text-red-600">Rp {fmt(inv.total)}</td>
                    <td className="py-3 px-4 text-red-500 font-medium">{fmtDate(inv.due_date)}</td>
                    <td className="py-3 px-4"><StatusBadge status={inv.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ───────────────────────── MAIN PAGE ─────────────────────────
const TABS = [
  { id: 'cod', label: 'COD', icon: Truck, hasPeriod: true },
  { id: 'courses', label: 'Kursus', icon: BookOpen, hasPeriod: true },
  { id: 'memberships', label: 'Membership', icon: Crown, hasPeriod: true },
  { id: 'donations', label: 'Donasi', icon: Heart, hasPeriod: true },
  { id: 'ppob', label: 'PPOB', icon: Zap, hasPeriod: true },
  { id: 'affiliate', label: 'Affiliate', icon: Share2, hasPeriod: true },
  { id: 'payment-plans', label: 'DP/Termin', icon: CreditCard, hasPeriod: false },
  { id: 'overdue', label: 'Overdue Invoice', icon: AlertCircle, hasPeriod: false },
];

export default function FinanceReports() {
  const [activeTab, setActiveTab] = useState('cod');
  const [period, setPeriod] = useState('30d');

  const current = TABS.find(t => t.id === activeTab);

  return (
    <div className="space-y-6" data-testid="finance-reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Laporan Keuangan</h1>
          <p className="text-sm text-slate-500 mt-1">8 laporan modul — COD, Kursus, Membership, Donasi, PPOB, Affiliate, DP/Termin, Invoice Overdue</p>
        </div>
        {current?.hasPeriod && (
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">7 Hari</SelectItem>
              <SelectItem value="30d">30 Hari</SelectItem>
              <SelectItem value="90d">90 Hari</SelectItem>
              <SelectItem value="1y">1 Tahun</SelectItem>
              <SelectItem value="all">Semua</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto gap-1 bg-slate-100 p-1">
          {TABS.map(({ id, label, icon: Icon }) => (
            <TabsTrigger key={id} value={id} className="flex items-center gap-1.5 text-xs">
              <Icon size={13} /> {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="cod"><CODReport period={period} /></TabsContent>
        <TabsContent value="courses"><CourseReport period={period} /></TabsContent>
        <TabsContent value="memberships"><MembershipReport period={period} /></TabsContent>
        <TabsContent value="donations"><DonationReport period={period} /></TabsContent>
        <TabsContent value="ppob"><PPOBReport period={period} /></TabsContent>
        <TabsContent value="affiliate"><AffiliateReport period={period} /></TabsContent>
        <TabsContent value="payment-plans"><PaymentPlanReport /></TabsContent>
        <TabsContent value="overdue"><OverdueInvoiceReport /></TabsContent>
      </Tabs>
    </div>
  );
}
